import {
  CONTENT_SCHEMA_VERSION,
  type AuthoredNumber,
  type BarrierDocument,
  type BlockDocument,
  type BossActionDocument,
  type BossActionShotDocument,
  type BossBehaviorDataDocument,
  type BossDocument,
  type BossProjectilePatternDocument,
  type BossRigDocument,
  type CleanserDocument,
  type CollisionRectangleDocument,
  type DialogueCatalogDocument,
  type DialogueStepDocument,
  type EncounterClearDocument,
  type EncounterDocument,
  type EnemyBehavior,
  type EnemySpawnDocument,
  type EnemyStatsDocument,
  type EntityBehaviorDataDocument,
  type EntityDefinitionDocument,
  type EntityVariantDocument,
  type GameManifestDocument,
  type GeneratorDocument,
  type GridPoint,
  type LegacyRoadDocument,
  type MilestoneUnlockDocument,
  type LevelCompletionDocument,
  type LevelDocument,
  type LevelEnvironmentDocument,
  type PixelPoint,
  type PickupDocument,
  type ProjectileDocument,
  type ProjectilePatternDocument,
  type RangedBehaviorDataDocument,
  type RandomValueDocument,
  type ShotChannelDocument,
  type ShotWaveDocument,
  type SlashDefinitionDocument,
  type SurvivalDebriefDocument,
  type SurvivalDocument,
  type TileCatalogDocument,
  type TileCollisionDocument,
  type TileDocument,
  type TriggerDocument,
  type TriggerTargetDocument,
  type WorldMapDocument,
  type WorldMapEdgeDocument,
  type WorldMapNodeDocument,
} from "./types";

const SEMANTIC_ID = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
const JSON_FILE = /^(?![./])(?:[^/\\]+\/)*[^/\\]+\.json$/;
const MISSIONS = ["mine", "cleanser", "crenson", "final"] as const;
const ICON_SIDES = ["top", "right", "bottom", "left"] as const;
const ROAD_ASSETS = [
  "straight",
  "turn-big-a",
  "turn-big-b",
  "turn-long",
  "turn-small-a",
  "turn-small-b",
] as const;
const SPEAKERS = [
  "asterus",
  "lumi",
  "crenson",
  "xenos-core",
  "unknown",
] as const;
const POSES = ["default", "alternate"] as const;
const ACTIONS = [
  "none",
  "no-text",
  "enable-enemy-attacks",
  "disable-enemy-attacks",
  "whiteout-on",
  "whiteout-off",
  "win",
  "boss-vanish",
  "boss-stop",
  "boss-resume",
  "activate-encounter",
  "camera-shake",
  "show-energy-crusher-unlock",
  "show-gravity-slicer-unlock",
  "show-megatron-unlock",
  "show-barrier-wing-unlock",
  "start-cleanser",
  "final-shard-burst",
  "boss-flash-and-resume",
  "start-boss-fight",
  "return-to-main-menu",
] as const;
const BEHAVIORS = [
  "ranged",
  "melee",
  "heavy-melee",
  "mine",
  "generator",
  "boss",
] as const;
const ZONES = [
  "day",
  "sunset",
  "night",
  "city-day",
  "underground",
  "city-night",
  "fortress",
  "vr",
] as const;
const BARRIERS = [
  "horizontal-start",
  "horizontal-end",
  "horizontal-middle",
  "vertical-start",
  "vertical-end",
  "vertical-middle",
] as const;
const MODES = ["encounter", "dialogue", "both"] as const;
const PICKUPS = ["large-xenos", "weapon-chip", "armour-chip"] as const;
const UNLOCK_MESSAGES = ["welcome", "secret-and-survival", "ultimate"] as const;
const LEVEL_KINDS = ["tutorial", "mission", "survival"] as const;
const PROJECTILE_SOUNDS = [
  "enemyShotOrb",
  "enemyShotSickle",
  "enemyShotLaser",
  "missileLaunch",
] as const;
const MUSIC_KEYS = [
  "level01",
  "level02",
  "level03",
  "level04",
  "level05",
  "level06",
  "bossIntro",
] as const;
const WEAPON_REWARDS = ["energy-crusher", "gravity-slicer"] as const;
const SCENES = ["level-select", "ending", "title"] as const;

export class ContentValidationError extends Error {
  constructor(
    readonly filename: string,
    readonly jsonPath: string,
    message: string,
  ) {
    super(`${filename}:${jsonPath}: ${message}`);
    this.name = "ContentValidationError";
  }
}

export function parseJsonValue(text: string, filename: string): unknown {
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new ContentValidationError(filename, "$", `invalid JSON: ${detail}`);
  }
  new JsonKeyScanner(text, filename).scan();
  return value;
}

class JsonKeyScanner {
  private position = 0;

  constructor(
    private readonly source: string,
    private readonly filename: string,
  ) {}

  scan(): void {
    this.skipWhitespace();
    this.scanValue("$");
  }

  private scanValue(path: string): void {
    this.skipWhitespace();
    const token = this.source[this.position];
    if (token === "{") {
      this.scanObject(path);
    } else if (token === "[") {
      this.scanArray(path);
    } else if (token === '\"') {
      this.scanString();
    } else {
      while (
        this.position < this.source.length &&
        !/[\s,\]}]/.test(this.source[this.position])
      ) {
        this.position++;
      }
    }
    this.skipWhitespace();
  }

  private scanObject(path: string): void {
    this.position++;
    this.skipWhitespace();
    if (this.source[this.position] === "}") {
      this.position++;
      return;
    }
    const keys = new Set<string>();
    while (this.position < this.source.length) {
      const key = this.scanString();
      const keyPath = /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)
        ? `${path}.${key}`
        : `${path}[${JSON.stringify(key)}]`;
      if (keys.has(key)) {
        throw new ContentValidationError(
          this.filename,
          keyPath,
          `duplicate object key '${key}'`,
        );
      }
      keys.add(key);
      this.skipWhitespace();
      this.position++;
      this.scanValue(keyPath);
      if (this.source[this.position] === "}") {
        this.position++;
        return;
      }
      this.position++;
      this.skipWhitespace();
    }
  }

  private scanArray(path: string): void {
    this.position++;
    this.skipWhitespace();
    if (this.source[this.position] === "]") {
      this.position++;
      return;
    }
    let index = 0;
    while (this.position < this.source.length) {
      this.scanValue(`${path}[${index}]`);
      if (this.source[this.position] === "]") {
        this.position++;
        return;
      }
      this.position++;
      this.skipWhitespace();
      index++;
    }
  }

  private scanString(): string {
    const start = this.position;
    this.position++;
    while (this.position < this.source.length) {
      const token = this.source[this.position++];
      if (token === "\\") {
        this.position++;
      } else if (token === '\"') {
        break;
      }
    }
    const value: unknown = JSON.parse(this.source.slice(start, this.position));
    if (typeof value !== "string") {
      throw new ContentValidationError(
        this.filename,
        "$",
        "invalid JSON string",
      );
    }
    this.skipWhitespace();
    return value;
  }

  private skipWhitespace(): void {
    while (/\s/.test(this.source[this.position] ?? "")) this.position++;
  }
}

export function parseGameManifest(
  value: unknown,
  filename = "game.json",
): GameManifestDocument {
  const p = new Parser(filename);
  const o = p.object(value, "$", [
    "schemaVersion",
    "worldMap",
    "globalDialogues",
    "tiles",
    "entities",
    "standaloneLevels",
  ]);
  p.version(o.schemaVersion, "$.schemaVersion");
  const worldMap = p.relativeJson(o.worldMap, "$.worldMap");
  const globalDialogues = p.relativeJson(
    o.globalDialogues,
    "$.globalDialogues",
  );
  const tiles = p.relativeJson(o.tiles, "$.tiles");
  const entities = p.uniqueStrings(
    o.entities,
    "$.entities",
    (item, path) => p.relativeJson(item, path),
    true,
  );
  const standaloneLevels = p.uniqueStrings(
    o.standaloneLevels,
    "$.standaloneLevels",
    (item, path) => p.relativeJson(item, path),
    true,
  );
  const documents = [
    { value: worldMap, path: "$.worldMap" },
    { value: globalDialogues, path: "$.globalDialogues" },
    { value: tiles, path: "$.tiles" },
    ...entities.map((entry, index) => ({
      value: entry,
      path: `$.entities[${index}]`,
    })),
    ...standaloneLevels.map((entry, index) => ({
      value: entry,
      path: `$.standaloneLevels[${index}]`,
    })),
  ];
  const seen = new Set<string>();
  for (const document of documents) {
    if (seen.has(document.value))
      p.fail(document.path, `duplicate document path '${document.value}'`);
    seen.add(document.value);
  }
  return {
    schemaVersion: CONTENT_SCHEMA_VERSION,
    worldMap,
    globalDialogues,
    tiles,
    entities,
    standaloneLevels,
  };
}

export function parseWorldMap(
  value: unknown,
  filename = "world-map.json",
): WorldMapDocument {
  const p = new Parser(filename);
  const o = p.object(value, "$", [
    "schemaVersion",
    "id",
    "nodes",
    "edges",
    "milestoneUnlocks",
  ]);
  p.version(o.schemaVersion, "$.schemaVersion");
  const nodes = p.array(
    o.nodes,
    "$.nodes",
    (item, path) => worldNode(p, item, path),
    true,
  );
  p.uniqueIds(nodes, "$.nodes", "world-map node");
  const edges = p.array(o.edges, "$.edges", (item, path) =>
    worldEdge(p, item, path),
  );
  const pairs = new Set<string>();
  edges.forEach((edge, index) => {
    const pair = `${edge.from}\u0000${edge.to}`;
    if (pairs.has(pair))
      p.fail(
        `$.edges[${index}]`,
        `duplicate edge '${edge.from}' -> '${edge.to}'`,
      );
    pairs.add(pair);
  });
  const milestoneUnlocks = p.array(
    o.milestoneUnlocks,
    "$.milestoneUnlocks",
    (item, path) => milestoneUnlock(p, item, path),
    true,
  );
  const milestoneValues = new Set<number>();
  milestoneUnlocks.forEach((unlock, index) => {
    if (milestoneValues.has(unlock.at)) {
      p.fail(
        `$.milestoneUnlocks[${index}].at`,
        `duplicate milestone '${unlock.at}'`,
      );
    }
    milestoneValues.add(unlock.at);
  });
  return {
    schemaVersion: CONTENT_SCHEMA_VERSION,
    id: p.semantic(o.id, "$.id"),
    nodes,
    edges,
    milestoneUnlocks,
  };
}

export function parseDialogueCatalog(
  value: unknown,
  filename = "dialogues.json",
): DialogueCatalogDocument {
  return dialogueCatalog(new Parser(filename), value, "$", true);
}

export function parseTileCatalog(
  value: unknown,
  filename = "tiles.json",
): TileCatalogDocument {
  const p = new Parser(filename);
  const o = p.object(value, "$", [
    "schemaVersion",
    "cellSize",
    "tileSize",
    "tiles",
  ]);
  p.version(o.schemaVersion, "$.schemaVersion");
  if (o.cellSize !== 64) p.fail("$.cellSize", "must be 64");
  if (o.tileSize !== 512) p.fail("$.tileSize", "must be 512");
  const tiles = p.array(
    o.tiles,
    "$.tiles",
    (item, path) => tile(p, item, path),
    true,
  );
  tiles.forEach((entry, index) => {
    if (entry.id !== index) {
      p.fail(`$.tiles[${index}].id`, `must equal its catalog index ${index}`);
    }
  });
  return {
    schemaVersion: CONTENT_SCHEMA_VERSION,
    cellSize: 64,
    tileSize: 512,
    tiles,
  };
}

export function parseEntityDocument(
  value: unknown,
  filename = "entity.json",
): EntityDefinitionDocument {
  const p = new Parser(filename);
  const o = p.object(value, "$", [
    "schemaVersion",
    "id",
    "name",
    "behavior",
    "sprite",
    "stats",
    "behaviorData?",
    "variants?",
  ]);
  p.version(o.schemaVersion, "$.schemaVersion");
  const behavior = p.oneOf(o.behavior, "$.behavior", BEHAVIORS);
  return {
    schemaVersion: CONTENT_SCHEMA_VERSION,
    id: p.semantic(o.id, "$.id"),
    name: p.nonEmpty(o.name, "$.name"),
    behavior,
    sprite: p.semantic(o.sprite, "$.sprite"),
    stats: enemyStats(p, o.stats, "$.stats"),
    ...p.optional(o, "behaviorData", "$", (item, path) =>
      behaviorData(p, item, path, behavior),
    ),
    ...p.optional(o, "variants", "$", (item, path) =>
      variants(p, item, path, behavior),
    ),
  };
}

export function parseLevelDocument(
  value: unknown,
  filename = "level.json",
): LevelDocument {
  const p = new Parser(filename);
  const o = p.object(value, "$", [
    "schemaVersion",
    "id",
    "name",
    "kind",
    "environment",
    "start",
    "encounters",
    "triggers",
    "generators?",
    "blocks?",
    "targets?",
    "obstacles?",
    "pickups?",
    "boss?",
    "cleanser?",
    "survival?",
    "hiddenCollectible?",
    "startDialogue?",
    "firstRunDialogueOnly?",
    "completion",
    "dialogues",
  ]);
  p.version(o.schemaVersion, "$.schemaVersion");
  const encounters = p.array(o.encounters, "$.encounters", (item, path) =>
    encounter(p, item, path),
  );
  p.uniqueIds(encounters, "$.encounters", "encounter");
  const generators = p.optional(o, "generators", "$", (item, path) =>
    p.array(item, path, (entry, entryPath) => generator(p, entry, entryPath)),
  );
  if (generators.generators !== undefined) {
    p.uniqueIds(generators.generators, "$.generators", "generator");
  }
  const kind = p.oneOf(o.kind, "$.kind", LEVEL_KINDS);
  const result: LevelDocument = {
    schemaVersion: CONTENT_SCHEMA_VERSION,
    id: p.semantic(o.id, "$.id"),
    name: p.nonEmpty(o.name, "$.name"),
    kind,
    environment: environment(p, o.environment, "$.environment"),
    start: p.grid(o.start, "$.start", true),
    encounters,
    triggers: p.array(o.triggers, "$.triggers", (item, path) =>
      trigger(p, item, path),
    ),
    ...generators,
    ...p.optional(o, "blocks", "$", (item, path) =>
      p.array(item, path, (entry, entryPath) => block(p, entry, entryPath)),
    ),
    ...p.optional(o, "targets", "$", (item, path) =>
      p.array(item, path, (entry, entryPath) => target(p, entry, entryPath)),
    ),
    ...p.optional(o, "obstacles", "$", (item, path) =>
      p.array(item, path, (entry, entryPath) =>
        collision(p, entry, entryPath, true),
      ),
    ),
    ...p.optional(o, "pickups", "$", (item, path) =>
      p.array(item, path, (entry, entryPath) => pickup(p, entry, entryPath)),
    ),
    ...p.optional(o, "boss", "$", (item, path) => boss(p, item, path)),
    ...p.optional(o, "cleanser", "$", (item, path) => cleanser(p, item, path)),
    ...p.optional(o, "survival", "$", (item, path) => survival(p, item, path)),
    ...p.optional(o, "hiddenCollectible", "$", (item, path) =>
      p.oneOf(item, path, ["weapon-chip", "armour-chip"] as const),
    ),
    ...p.optional(o, "startDialogue", "$", (item, path) =>
      p.semantic(item, path),
    ),
    ...p.optional(o, "firstRunDialogueOnly", "$", (item, path) =>
      p.bool(item, path),
    ),
    completion: completion(p, o.completion, "$.completion"),
    dialogues: dialogueCatalog(p, o.dialogues, "$.dialogues", true),
  };
  if (kind === "survival" && result.survival === undefined)
    p.fail("$.survival", "is required for survival levels");
  if (kind !== "survival" && result.survival !== undefined)
    p.fail("$.survival", `is invalid for '${kind}' levels`);
  return result;
}

export const parseGameManifestJson = (
  text: string,
  filename = "game.json",
): GameManifestDocument =>
  parseGameManifest(parseJsonValue(text, filename), filename);
export const parseWorldMapJson = (
  text: string,
  filename = "world-map.json",
): WorldMapDocument => parseWorldMap(parseJsonValue(text, filename), filename);
export const parseDialogueCatalogJson = (
  text: string,
  filename = "dialogues.json",
): DialogueCatalogDocument =>
  parseDialogueCatalog(parseJsonValue(text, filename), filename);
export const parseTileCatalogJson = (
  text: string,
  filename = "tiles.json",
): TileCatalogDocument =>
  parseTileCatalog(parseJsonValue(text, filename), filename);
export const parseEntityDocumentJson = (
  text: string,
  filename = "entity.json",
): EntityDefinitionDocument =>
  parseEntityDocument(parseJsonValue(text, filename), filename);
export const parseLevelDocumentJson = (
  text: string,
  filename = "level.json",
): LevelDocument =>
  parseLevelDocument(parseJsonValue(text, filename), filename);

function worldNode(
  p: Parser,
  value: unknown,
  path: string,
): WorldMapNodeDocument {
  const o = p.object(value, path, [
    "id",
    "level",
    "x",
    "y",
    "label",
    "area",
    "mission?",
    "iconSide?",
    "initial?",
  ]);
  const mission = p.optional(o, "mission", path, (item, itemPath) =>
    p.oneOf(item, itemPath, MISSIONS),
  );
  const iconSide = p.optional(o, "iconSide", path, (item, itemPath) =>
    p.oneOf(item, itemPath, ICON_SIDES),
  );
  if ((mission.mission === undefined) !== (iconSide.iconSide === undefined)) {
    p.fail(
      path,
      "mission and iconSide must either both be present or both be absent",
    );
  }
  return {
    id: p.semantic(o.id, `${path}.id`),
    level: p.relativeJson(o.level, `${path}.level`),
    x: p.finite(o.x, `${path}.x`),
    y: p.finite(o.y, `${path}.y`),
    label: p.nonEmpty(o.label, `${path}.label`),
    area: p.nonEmpty(o.area, `${path}.area`),
    ...mission,
    ...iconSide,
    ...p.optional(o, "initial", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
  };
}

function milestoneUnlock(
  p: Parser,
  value: unknown,
  path: string,
): MilestoneUnlockDocument {
  const o = p.object(value, path, [
    "at",
    "next",
    "message",
    "unlockLevel?",
    "unlockSurvival?",
  ]);
  const at = p.nonNegativeInt(o.at, `${path}.at`);
  const next = p.positiveInt(o.next, `${path}.next`);
  if (next <= at) p.fail(`${path}.next`, "must exceed at");
  if (Object.hasOwn(o, "unlockSurvival") && o.unlockSurvival !== true) {
    p.fail(`${path}.unlockSurvival`, "must be true when present");
  }
  return {
    at,
    next,
    message: p.oneOf(o.message, `${path}.message`, UNLOCK_MESSAGES),
    ...p.optional(o, "unlockLevel", path, (item, itemPath) =>
      p.semantic(item, itemPath),
    ),
    ...(o.unlockSurvival === true ? { unlockSurvival: true as const } : {}),
  };
}

function worldEdge(
  p: Parser,
  value: unknown,
  path: string,
): WorldMapEdgeDocument {
  const o = p.object(value, path, ["from", "to", "road?"]);
  return {
    from: p.semantic(o.from, `${path}.from`),
    to: p.semantic(o.to, `${path}.to`),
    ...p.optional(o, "road", path, (item, itemPath) => road(p, item, itemPath)),
  };
}

function road(p: Parser, value: unknown, path: string): LegacyRoadDocument {
  const o = p.object(value, path, ["asset", "rotate?", "flipX?", "flipY?"]);
  return {
    asset: p.oneOf(o.asset, `${path}.asset`, ROAD_ASSETS),
    ...p.optional(o, "rotate", path, (item, itemPath) =>
      p.finite(item, itemPath),
    ),
    ...p.optional(o, "flipX", path, (item, itemPath) => p.bool(item, itemPath)),
    ...p.optional(o, "flipY", path, (item, itemPath) => p.bool(item, itemPath)),
  };
}

function dialogueCatalog(
  p: Parser,
  value: unknown,
  path: string,
  allowEmpty: boolean,
): DialogueCatalogDocument {
  const source = p.dictionary(value, path, allowEmpty);
  const result: Record<string, readonly DialogueStepDocument[]> = Object.create(
    null,
  ) as Record<string, readonly DialogueStepDocument[]>;
  for (const [id, script] of Object.entries(source)) {
    const scriptPath = p.keyPath(path, id);
    p.semantic(id, scriptPath);
    result[id] = p.array(
      script,
      scriptPath,
      (item, itemPath) => dialogueStep(p, item, itemPath),
      true,
    );
  }
  return result;
}

function dialogueStep(
  p: Parser,
  value: unknown,
  path: string,
): DialogueStepDocument {
  const o = p.object(value, path, [
    "text?",
    "speaker?",
    "pose?",
    "readyFrames?",
    "action?",
    "encounter?",
    "textBox?",
  ]);
  const action = p.optional(o, "action", path, (item, itemPath) =>
    p.oneOf(item, itemPath, ACTIONS),
  );
  const encounter = p.optional(o, "encounter", path, (item, itemPath) =>
    p.semantic(item, itemPath),
  );
  const activatesEncounter = action.action === "activate-encounter";
  if (activatesEncounter !== (encounter.encounter !== undefined)) {
    p.fail(
      `${path}.encounter`,
      activatesEncounter
        ? "is required for activate-encounter"
        : "is only valid for activate-encounter",
    );
  }
  return {
    ...p.optional(o, "text", path, (item, itemPath) =>
      p.string(item, itemPath),
    ),
    ...p.optional(o, "speaker", path, (item, itemPath) =>
      p.oneOf(item, itemPath, SPEAKERS),
    ),
    ...p.optional(o, "pose", path, (item, itemPath) =>
      p.oneOf(item, itemPath, POSES),
    ),
    ...p.optional(o, "readyFrames", path, (item, itemPath) =>
      p.nonNegativeInt(item, itemPath),
    ),
    ...action,
    ...encounter,
    ...p.optional(o, "textBox", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
  };
}

function tile(p: Parser, value: unknown, path: string): TileDocument {
  const o = p.object(value, path, ["id", "obstacles"]);
  return {
    id: p.nonNegativeInt(o.id, `${path}.id`),
    obstacles: p.array(o.obstacles, `${path}.obstacles`, (item, itemPath) =>
      tileCollision(p, item, itemPath),
    ),
  };
}

function tileCollision(
  p: Parser,
  value: unknown,
  path: string,
): TileCollisionDocument {
  const o = p.object(value, path, ["from", "to"]);
  const from = p.integerGrid(o.from, `${path}.from`, true);
  const to = p.integerGrid(o.to, `${path}.to`, true);
  checkRectangle(p, from, to, path);
  if (to.x > 8 || to.y > 8)
    p.fail(
      `${path}.to`,
      "tile-local collision must remain inside the 8x8 tile",
    );
  return { from, to };
}

function behaviorData(
  p: Parser,
  value: unknown,
  path: string,
  _behavior: EnemyBehavior,
): EntityBehaviorDataDocument {
  const object = p.dictionary(value, path, false);
  if (Object.hasOwn(object, "projectilePattern"))
    return rangedData(p, value, path);
  if (Object.hasOwn(object, "rig")) return bossData(p, value, path);
  p.fail(
    path,
    "expected ranged projectilePattern data or boss rig/actions/patterns data",
  );
}

function rangedData(
  p: Parser,
  value: unknown,
  path: string,
): RangedBehaviorDataDocument {
  const o = p.object(value, path, ["channel", "projectilePattern"]);
  return {
    channel: p.semantic(o.channel, `${path}.channel`),
    projectilePattern: projectilePattern(
      p,
      o.projectilePattern,
      `${path}.projectilePattern`,
    ),
  };
}

function bossData(
  p: Parser,
  value: unknown,
  path: string,
): BossBehaviorDataDocument {
  const o = p.object(value, path, [
    "rig",
    "actions",
    "patterns",
    "warpOnRoll?",
  ]);
  return {
    rig: bossRig(p, o.rig, `${path}.rig`),
    actions: record(
      p,
      o.actions,
      `${path}.actions`,
      (item, itemPath) => bossAction(p, item, itemPath),
      true,
    ),
    patterns: record(
      p,
      o.patterns,
      `${path}.patterns`,
      (item, itemPath) => bossPattern(p, item, itemPath),
      true,
    ),
    ...p.optional(o, "warpOnRoll", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
  };
}

function bossRig(p: Parser, value: unknown, path: string): BossRigDocument {
  const o = p.object(value, path, ["shotX", "shotY", "explosionRadius"]);
  return {
    shotX: p.finite(o.shotX, `${path}.shotX`),
    shotY: p.finite(o.shotY, `${path}.shotY`),
    explosionRadius: p.positive(o.explosionRadius, `${path}.explosionRadius`),
  };
}

function bossAction(
  p: Parser,
  value: unknown,
  path: string,
): BossActionDocument {
  const o = p.object(value, path, ["idleFrames?", "shot?", "loopTo?"]);
  if (Object.keys(o).length === 0)
    p.fail(path, "boss action must not be empty");
  return {
    ...p.optional(o, "idleFrames", path, (item, itemPath) =>
      p.nonNegativeInt(item, itemPath),
    ),
    ...p.optional(o, "shot", path, (item, itemPath) =>
      bossActionShot(p, item, itemPath),
    ),
    ...p.optional(o, "loopTo", path, (item, itemPath) =>
      p.nonNegativeInt(item, itemPath),
    ),
  };
}

function bossActionShot(
  p: Parser,
  value: unknown,
  path: string,
): BossActionShotDocument {
  const o = p.object(value, path, [
    "pattern",
    "spacingFrames",
    "bursts",
    "resetAngle?",
  ]);
  return {
    pattern: p.semantic(o.pattern, `${path}.pattern`),
    spacingFrames: p.positiveInt(o.spacingFrames, `${path}.spacingFrames`),
    bursts: p.positiveInt(o.bursts, `${path}.bursts`),
    ...p.optional(o, "resetAngle", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
  };
}

function bossPattern(
  p: Parser,
  value: unknown,
  path: string,
): BossProjectilePatternDocument {
  const o = p.object(value, path, [
    "projectiles",
    "spinDegrees?",
    "sound?",
    "missileSound?",
  ]);
  return {
    projectiles: p.array(
      o.projectiles,
      `${path}.projectiles`,
      (item, itemPath) => projectile(p, item, itemPath),
      true,
    ),
    ...p.optional(o, "spinDegrees", path, (item, itemPath) =>
      p.finite(item, itemPath),
    ),
    ...p.optional(o, "sound", path, (item, itemPath) =>
      p.oneOf(item, itemPath, PROJECTILE_SOUNDS),
    ),
    ...p.optional(o, "missileSound", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
  };
}

function projectilePattern(
  p: Parser,
  value: unknown,
  path: string,
): ProjectilePatternDocument {
  const o = p.object(value, path, [
    "projectiles",
    "sound?",
    "finalRepeatProjectile?",
    "finalRepeatSound?",
  ]);
  if (
    Object.hasOwn(o, "finalRepeatSound") &&
    !Object.hasOwn(o, "finalRepeatProjectile")
  ) {
    p.fail(`${path}.finalRepeatSound`, "requires finalRepeatProjectile");
  }
  return {
    projectiles: p.array(
      o.projectiles,
      `${path}.projectiles`,
      (item, itemPath) => projectile(p, item, itemPath),
      true,
    ),
    ...p.optional(o, "sound", path, (item, itemPath) =>
      p.oneOf(item, itemPath, PROJECTILE_SOUNDS),
    ),
    ...p.optional(o, "finalRepeatProjectile", path, (item, itemPath) =>
      projectile(p, item, itemPath),
    ),
    ...p.optional(o, "finalRepeatSound", path, (item, itemPath) =>
      p.oneOf(item, itemPath, PROJECTILE_SOUNDS),
    ),
  };
}

function projectile(
  p: Parser,
  value: unknown,
  path: string,
): ProjectileDocument {
  const o = p.object(value, path, ["effect", "angle", "absolute?"]);
  return {
    effect: p.semantic(o.effect, `${path}.effect`),
    angle: p.finite(o.angle, `${path}.angle`),
    ...p.optional(o, "absolute", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
  };
}

function variants(
  p: Parser,
  value: unknown,
  path: string,
  behavior: EnemyBehavior,
): Readonly<Record<string, EntityVariantDocument>> {
  return record(
    p,
    value,
    path,
    (item, itemPath) => {
      const o = p.object(item, itemPath, [
        "name",
        "sprite",
        "stats",
        "behaviorData?",
      ]);
      return {
        name: p.nonEmpty(o.name, `${itemPath}.name`),
        sprite: p.semantic(o.sprite, `${itemPath}.sprite`),
        stats: enemyStats(p, o.stats, `${itemPath}.stats`),
        ...p.optional(o, "behaviorData", itemPath, (data, dataPath) =>
          behaviorData(p, data, dataPath, behavior),
        ),
      };
    },
    true,
    true,
  );
}

function enemyStats(
  p: Parser,
  value: unknown,
  path: string,
): EnemyStatsDocument {
  const o = p.object(value, path, [
    "tileSize",
    "bitmapWidth",
    "hitRadius",
    "armour?",
    "speed",
    "rollSpeed?",
    "idleFrameCount",
    "rollAnimationOffset",
    "rollFrameCount",
    "preferredDistance",
    "initialActionDelay?",
    "rollLimit?",
    "passesWalls?",
    "animationSpeed?",
    "pausesBetweenMoves?",
    "startsMoving?",
    "projectileOffset?",
    "rewardPoints?",
    "xenosDropChance?",
    "healthDropChance?",
    "slash?",
  ]);
  return {
    tileSize: p.positiveInt(o.tileSize, `${path}.tileSize`),
    bitmapWidth: p.positiveInt(o.bitmapWidth, `${path}.bitmapWidth`),
    hitRadius: p.nonNegative(o.hitRadius, `${path}.hitRadius`),
    ...p.optional(o, "armour", path, (item, itemPath) =>
      p.nonNegative(item, itemPath),
    ),
    speed: p.nonNegative(o.speed, `${path}.speed`),
    ...p.optional(o, "rollSpeed", path, (item, itemPath) =>
      p.nonNegative(item, itemPath),
    ),
    idleFrameCount: p.nonNegativeInt(
      o.idleFrameCount,
      `${path}.idleFrameCount`,
    ),
    rollAnimationOffset: p.nonNegativeInt(
      o.rollAnimationOffset,
      `${path}.rollAnimationOffset`,
    ),
    rollFrameCount: p.nonNegativeInt(
      o.rollFrameCount,
      `${path}.rollFrameCount`,
    ),
    preferredDistance: authoredNumber(
      p,
      o.preferredDistance,
      `${path}.preferredDistance`,
    ),
    ...p.optional(o, "initialActionDelay", path, (item, itemPath) =>
      authoredNumber(p, item, itemPath),
    ),
    ...p.optional(o, "rollLimit", path, (item, itemPath) =>
      p.nonNegativeInt(item, itemPath),
    ),
    ...p.optional(o, "passesWalls", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
    ...p.optional(o, "animationSpeed", path, (item, itemPath) =>
      p.nonNegative(item, itemPath),
    ),
    ...p.optional(o, "pausesBetweenMoves", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
    ...p.optional(o, "startsMoving", path, (item, itemPath) =>
      p.bool(item, itemPath),
    ),
    ...p.optional(o, "projectileOffset", path, (item, itemPath) =>
      pixelPoint(p, item, itemPath),
    ),
    ...p.optional(o, "rewardPoints", path, (item, itemPath) =>
      p.nonNegativeInt(item, itemPath),
    ),
    ...p.optional(o, "xenosDropChance", path, (item, itemPath) =>
      p.nonNegative(item, itemPath),
    ),
    ...p.optional(o, "healthDropChance", path, (item, itemPath) =>
      p.nonNegative(item, itemPath),
    ),
    ...p.optional(o, "slash", path, (item, itemPath) =>
      slash(p, item, itemPath),
    ),
  };
}

function pixelPoint(p: Parser, value: unknown, path: string): PixelPoint {
  const o = p.object(value, path, ["x", "y"]);
  return {
    x: p.finite(o.x, `${path}.x`),
    y: p.finite(o.y, `${path}.y`),
  };
}

function authoredNumber(
  p: Parser,
  value: unknown,
  path: string,
): AuthoredNumber {
  if (typeof value === "number") return p.finite(value, path);
  const o = p.object(value, path, ["minExclusive", "maxInclusive", "step?"]);
  const result: RandomValueDocument = {
    minExclusive: p.finite(o.minExclusive, `${path}.minExclusive`),
    maxInclusive: p.finite(o.maxInclusive, `${path}.maxInclusive`),
    ...p.optional(o, "step", path, (item, itemPath) =>
      p.positive(item, itemPath),
    ),
  };
  if (result.maxInclusive <= result.minExclusive)
    p.fail(path, "maxInclusive must exceed minExclusive");
  return result;
}

function slash(
  p: Parser,
  value: unknown,
  path: string,
): SlashDefinitionDocument {
  const o = p.object(value, path, [
    "animationOffset",
    "frameCount",
    "freezeFrame",
    "range",
    "frequency",
  ]);
  return {
    animationOffset: p.nonNegativeInt(
      o.animationOffset,
      `${path}.animationOffset`,
    ),
    frameCount: p.positiveInt(o.frameCount, `${path}.frameCount`),
    freezeFrame: p.nonNegativeInt(o.freezeFrame, `${path}.freezeFrame`),
    range: p.nonNegative(o.range, `${path}.range`),
    frequency: p.positiveInt(o.frequency, `${path}.frequency`),
  };
}

function environment(
  p: Parser,
  value: unknown,
  path: string,
): LevelEnvironmentDocument {
  const o = p.object(value, path, [
    "zone",
    "music",
    "rain?",
    "fortressStage?",
    "tiles",
  ]);
  const tiles = p.array(
    o.tiles,
    `${path}.tiles`,
    (item, itemPath) =>
      p.array(
        item,
        itemPath,
        (tileId, tilePath) =>
          tileId === null ? null : p.nonNegativeInt(tileId, tilePath),
        true,
      ),
    true,
  );
  const width = tiles[0].length;
  tiles.forEach((row, index) => {
    if (row.length !== width)
      p.fail(
        `${path}.tiles[${index}]`,
        `row length ${row.length}; expected ${width}`,
      );
  });
  const zone = p.oneOf(o.zone, `${path}.zone`, ZONES);
  const stage = p.optional(o, "fortressStage", path, (item, itemPath) => {
    const result = p.positiveInt(item, itemPath);
    if (result > 3) p.fail(itemPath, "must be 1, 2, or 3");
    return result as 1 | 2 | 3;
  });
  return {
    zone,
    music: p.oneOf(o.music, `${path}.music`, MUSIC_KEYS),
    ...p.optional(o, "rain", path, (item, itemPath) => p.bool(item, itemPath)),
    ...stage,
    tiles,
  };
}

function encounter(p: Parser, value: unknown, path: string): EncounterDocument {
  const o = p.object(value, path, ["id", "spawns?", "barriers?", "onClear?"]);
  return {
    id: p.semantic(o.id, `${path}.id`),
    ...p.optional(o, "spawns", path, (item, itemPath) =>
      p.array(item, itemPath, (entry, entryPath) => spawn(p, entry, entryPath)),
    ),
    ...p.optional(o, "barriers", path, (item, itemPath) =>
      p.array(item, itemPath, (entry, entryPath) =>
        barrier(p, entry, entryPath),
      ),
    ),
    ...p.optional(o, "onClear", path, (item, itemPath) =>
      encounterClear(p, item, itemPath),
    ),
  };
}

function spawn(p: Parser, value: unknown, path: string): EnemySpawnDocument {
  const o = p.object(value, path, ["at", "entity", "count?"]);
  return {
    at: p.grid(o.at, `${path}.at`, true),
    entity: p.semantic(o.entity, `${path}.entity`),
    ...p.optional(o, "count", path, (item, itemPath) =>
      p.positiveInt(item, itemPath),
    ),
  };
}

function barrier(p: Parser, value: unknown, path: string): BarrierDocument {
  const o = p.object(value, path, ["at", "variant"]);
  return {
    at: p.grid(o.at, `${path}.at`, true),
    variant: p.oneOf(o.variant, `${path}.variant`, BARRIERS),
  };
}

function encounterClear(
  p: Parser,
  value: unknown,
  path: string,
): EncounterClearDocument {
  const o = p.object(value, path, ["activate?", "dialogue?"]);
  if (!Object.hasOwn(o, "activate") && !Object.hasOwn(o, "dialogue"))
    p.fail(path, "must activate an encounter or a dialogue");
  return {
    ...p.optional(o, "activate", path, (item, itemPath) =>
      p.semantic(item, itemPath),
    ),
    ...p.optional(o, "dialogue", path, (item, itemPath) =>
      p.semantic(item, itemPath),
    ),
  };
}

function trigger(p: Parser, value: unknown, path: string): TriggerDocument {
  const o = p.object(value, path, ["at", "mode", "encounter?", "dialogue?"]);
  const mode = p.oneOf(o.mode, `${path}.mode`, MODES);
  const encounterId = p.optional(o, "encounter", path, (item, itemPath) =>
    p.semantic(item, itemPath),
  );
  const dialogueId = p.optional(o, "dialogue", path, (item, itemPath) =>
    p.semantic(item, itemPath),
  );
  const needsEncounter = mode === "encounter" || mode === "both";
  const needsDialogue = mode === "dialogue" || mode === "both";
  if (needsEncounter !== (encounterId.encounter !== undefined))
    p.fail(
      `${path}.encounter`,
      needsEncounter
        ? `is required in '${mode}' mode`
        : "is invalid in dialogue mode",
    );
  if (needsDialogue !== (dialogueId.dialogue !== undefined))
    p.fail(
      `${path}.dialogue`,
      needsDialogue
        ? `is required in '${mode}' mode`
        : "is invalid in encounter mode",
    );
  return {
    at: p.grid(o.at, `${path}.at`, true),
    mode,
    ...encounterId,
    ...dialogueId,
  };
}

function generator(p: Parser, value: unknown, path: string): GeneratorDocument {
  const o = p.object(value, path, ["id", "at"]);
  return {
    id: p.semantic(o.id, `${path}.id`),
    at: p.grid(o.at, `${path}.at`, true),
  };
}

function block(p: Parser, value: unknown, path: string): BlockDocument {
  const o = p.object(value, path, ["at", "variant"]);
  return {
    at: p.grid(o.at, `${path}.at`, true),
    variant: p.oneOf(o.variant, `${path}.variant`, ["a", "b"] as const),
  };
}

function target(
  p: Parser,
  value: unknown,
  path: string,
): TriggerTargetDocument {
  const o = p.object(value, path, ["at"]);
  return { at: p.grid(o.at, `${path}.at`, true) };
}

function collision(
  p: Parser,
  value: unknown,
  path: string,
  bucketAllowed: boolean,
): CollisionRectangleDocument {
  const o = p.object(
    value,
    path,
    bucketAllowed ? ["from", "to", "bucket?"] : ["from", "to"],
  );
  const from = nonNegativePoint(p, o.from, `${path}.from`);
  const to = nonNegativePoint(p, o.to, `${path}.to`);
  checkRectangle(p, from, to, path);
  return {
    from,
    to,
    ...p.optional(o, "bucket", path, (item, itemPath) =>
      p.integerGrid(item, itemPath, true),
    ),
  };
}

function nonNegativePoint(p: Parser, value: unknown, path: string): GridPoint {
  const o = p.object(value, path, ["x", "y"]);
  return {
    x: p.nonNegative(o.x, `${path}.x`),
    y: p.nonNegative(o.y, `${path}.y`),
  };
}

function pickup(p: Parser, value: unknown, path: string): PickupDocument {
  const o = p.object(value, path, ["at", "kind"]);
  return {
    at: p.grid(o.at, `${path}.at`, true),
    kind: p.oneOf(o.kind, `${path}.kind`, PICKUPS),
  };
}

function boss(p: Parser, value: unknown, path: string): BossDocument {
  const o = p.object(value, path, [
    "at",
    "entity",
    "variant",
    "weaponReward?",
    "weaponUnlockDialogue?",
    "lowHealthDialogue?",
    "defeatedDialogue?",
  ]);
  if (
    Object.hasOwn(o, "weaponReward") !==
    Object.hasOwn(o, "weaponUnlockDialogue")
  ) {
    p.fail(
      path,
      "weaponReward and weaponUnlockDialogue must be authored together",
    );
  }
  return {
    at: p.grid(o.at, `${path}.at`, true),
    entity: p.semantic(o.entity, `${path}.entity`),
    variant: p.semantic(o.variant, `${path}.variant`),
    ...p.optional(o, "weaponReward", path, (item, itemPath) =>
      p.oneOf(item, itemPath, WEAPON_REWARDS),
    ),
    ...p.optional(o, "weaponUnlockDialogue", path, (item, itemPath) =>
      p.semantic(item, itemPath),
    ),
    ...p.optional(o, "lowHealthDialogue", path, (item, itemPath) =>
      p.semantic(item, itemPath),
    ),
    ...p.optional(o, "defeatedDialogue", path, (item, itemPath) =>
      p.semantic(item, itemPath),
    ),
  };
}

function cleanser(p: Parser, value: unknown, path: string): CleanserDocument {
  const o = p.object(value, path, ["enabled"]);
  if (o.enabled !== true) p.fail(`${path}.enabled`, "must be true");
  return { enabled: true };
}

function survival(p: Parser, value: unknown, path: string): SurvivalDocument {
  const o = p.object(value, path, [
    "loop",
    "harderAfterWave",
    "shotWaves",
    "weaponPreset",
    "debriefs",
  ]);
  const debriefs = p.array(
    o.debriefs,
    `${path}.debriefs`,
    (item, itemPath) => debrief(p, item, itemPath),
    true,
  );
  let previous = -Infinity;
  debriefs.forEach((entry, index) => {
    if (entry.belowScore === null && index !== debriefs.length - 1)
      p.fail(
        `${path}.debriefs[${index}].belowScore`,
        "null catch-all must be last",
      );
    if (entry.belowScore !== null) {
      if (entry.belowScore <= previous)
        p.fail(
          `${path}.debriefs[${index}].belowScore`,
          "thresholds must increase strictly",
        );
      previous = entry.belowScore;
    }
  });
  if (debriefs[debriefs.length - 1].belowScore !== null)
    p.fail(`${path}.debriefs`, "must end in a null catch-all threshold");
  return {
    loop: p.uniqueStrings(
      o.loop,
      `${path}.loop`,
      (item, itemPath) => p.semantic(item, itemPath),
      true,
    ),
    harderAfterWave: p.positiveInt(
      o.harderAfterWave,
      `${path}.harderAfterWave`,
    ),
    shotWaves: p.array(
      o.shotWaves,
      `${path}.shotWaves`,
      (item, itemPath) => shotWave(p, item, itemPath),
      true,
    ),
    weaponPreset: p.numberArray(
      o.weaponPreset,
      `${path}.weaponPreset`,
      true,
      true,
    ),
    debriefs,
  };
}

function shotWave(p: Parser, value: unknown, path: string): ShotWaveDocument {
  const o = p.object(value, path, ["channels"]);
  const channels = p.array(
    o.channels,
    `${path}.channels`,
    (item, itemPath) => shotChannel(p, item, itemPath),
    true,
  );
  p.uniqueIds(channels, `${path}.channels`, "shot channel");
  return { channels };
}

function shotChannel(
  p: Parser,
  value: unknown,
  path: string,
): ShotChannelDocument {
  const o = p.object(value, path, [
    "id",
    "counter",
    "limit",
    "repeatLimit",
    "repeatSpacing?",
  ]);
  return {
    id: p.semantic(o.id, `${path}.id`),
    counter: p.nonNegativeInt(o.counter, `${path}.counter`),
    limit: p.positiveInt(o.limit, `${path}.limit`),
    repeatLimit: p.nonNegativeInt(o.repeatLimit, `${path}.repeatLimit`),
    ...p.optional(o, "repeatSpacing", path, (item, itemPath) =>
      p.positiveInt(item, itemPath),
    ),
  };
}

function debrief(
  p: Parser,
  value: unknown,
  path: string,
): SurvivalDebriefDocument {
  const o = p.object(value, path, ["belowScore", "dialogue"]);
  return {
    belowScore:
      o.belowScore === null
        ? null
        : p.positiveInt(o.belowScore, `${path}.belowScore`),
    dialogue: p.semantic(o.dialogue, `${path}.dialogue`),
  };
}

function completion(
  p: Parser,
  value: unknown,
  path: string,
): LevelCompletionDocument {
  const o = p.object(value, path, ["scene", "storyMilestone?"]);
  return {
    scene: p.oneOf(o.scene, `${path}.scene`, SCENES),
    ...p.optional(o, "storyMilestone", path, (item, itemPath) =>
      p.nonNegativeInt(item, itemPath),
    ),
  };
}

function record<T>(
  p: Parser,
  value: unknown,
  path: string,
  parse: (value: unknown, path: string) => T,
  requireNonEmpty: boolean,
  semanticKeys = false,
): Readonly<Record<string, T>> {
  const source = p.dictionary(value, path, !requireNonEmpty);
  const result: Record<string, T> = Object.create(null) as Record<string, T>;
  for (const [key, item] of Object.entries(source)) {
    const itemPath = p.keyPath(path, key);
    if (semanticKeys) p.semantic(key, itemPath);
    else if (key.length === 0) p.fail(itemPath, "record key must not be empty");
    result[key] = parse(item, itemPath);
  }
  return result;
}

function checkRectangle(
  p: Parser,
  from: GridPoint,
  to: GridPoint,
  path: string,
): void {
  if (to.x <= from.x) p.fail(`${path}.to.x`, "must exceed from.x");
  if (to.y <= from.y) p.fail(`${path}.to.y`, "must exceed from.y");
}

class Parser {
  constructor(readonly filename: string) {}

  fail(path: string, message: string): never {
    throw new ContentValidationError(this.filename, path, message);
  }

  object(
    value: unknown,
    path: string,
    fields: readonly string[],
  ): Record<string, unknown> {
    const result = this.dictionary(value, path, true);
    const allowed = new Set(
      fields.map((field) => (field.endsWith("?") ? field.slice(0, -1) : field)),
    );
    for (const key of Object.keys(result))
      if (!allowed.has(key))
        this.fail(this.keyPath(path, key), "unknown field");
    for (const field of fields)
      if (!field.endsWith("?") && !Object.hasOwn(result, field))
        this.fail(`${path}.${field}`, "missing required field");
    return result;
  }

  dictionary(
    value: unknown,
    path: string,
    allowEmpty: boolean,
  ): Record<string, unknown> {
    if (!isRecord(value)) this.fail(path, "expected an object");
    if (!allowEmpty && Object.keys(value).length === 0)
      this.fail(path, "object must not be empty");
    return value;
  }

  array<T>(
    value: unknown,
    path: string,
    parse: (value: unknown, path: string) => T,
    requireNonEmpty = false,
  ): readonly T[] {
    if (!Array.isArray(value)) this.fail(path, "expected an array");
    if (requireNonEmpty && value.length === 0)
      this.fail(path, "array must not be empty");
    return value.map((item, index) => parse(item, `${path}[${index}]`));
  }

  optional<K extends string, T>(
    object: Record<string, unknown>,
    key: K,
    parentPath: string,
    parse: (value: unknown, path: string) => T,
  ): Readonly<Partial<Record<K, T>>> {
    if (!Object.hasOwn(object, key)) {
      return {} as Readonly<Partial<Record<K, T>>>;
    }
    return {
      [key]: parse(object[key], this.keyPath(parentPath, key)),
    } as Partial<Record<K, T>>;
  }

  uniqueStrings(
    value: unknown,
    path: string,
    parse: (value: unknown, path: string) => string,
    requireNonEmpty = false,
  ): readonly string[] {
    const result = this.array(value, path, parse, requireNonEmpty);
    const seen = new Set<string>();
    result.forEach((item, index) => {
      if (seen.has(item))
        this.fail(`${path}[${index}]`, `duplicate value '${item}'`);
      seen.add(item);
    });
    return result;
  }

  uniqueIds<T extends { readonly id: string }>(
    values: readonly T[],
    path: string,
    label: string,
  ): void {
    const seen = new Set<string>();
    values.forEach((item, index) => {
      if (seen.has(item.id))
        this.fail(`${path}[${index}].id`, `duplicate ${label} id '${item.id}'`);
      seen.add(item.id);
    });
  }

  numberArray(
    value: unknown,
    path: string,
    nonNegativeIntegers: boolean,
    requireNonEmpty = false,
  ): readonly number[] {
    return this.array(
      value,
      path,
      (item, itemPath) =>
        nonNegativeIntegers
          ? this.nonNegativeInt(item, itemPath)
          : this.finite(item, itemPath),
      requireNonEmpty,
    );
  }

  version(value: unknown, path: string): void {
    if (value !== CONTENT_SCHEMA_VERSION)
      this.fail(
        path,
        `unsupported schema version ${String(value)}; expected ${CONTENT_SCHEMA_VERSION}`,
      );
  }

  string(value: unknown, path: string): string {
    if (typeof value !== "string") this.fail(path, "expected a string");
    return value;
  }

  nonEmpty(value: unknown, path: string): string {
    const result = this.string(value, path);
    if (result.trim().length === 0) this.fail(path, "string must not be empty");
    return result;
  }

  semantic(value: unknown, path: string): string {
    const result = this.string(value, path);
    if (!SEMANTIC_ID.test(result))
      this.fail(path, "expected a lowercase semantic id");
    return result;
  }

  relativeJson(value: unknown, path: string): string {
    const result = this.string(value, path);
    if (!JSON_FILE.test(result))
      this.fail(path, "expected a safe relative .json path");
    for (const part of result.split("/")) {
      let decoded: string;
      try {
        decoded = decodeURIComponent(part);
      } catch {
        this.fail(path, "path contains malformed percent encoding");
      }
      if (
        decoded === "." ||
        decoded === ".." ||
        decoded.includes("/") ||
        decoded.includes("\\") ||
        decoded.includes("?") ||
        decoded.includes("#") ||
        decoded.includes(":") ||
        decoded.includes("\0")
      ) {
        this.fail(path, "path must remain inside the manifest directory");
      }
    }
    return result;
  }

  bool(value: unknown, path: string): boolean {
    if (typeof value !== "boolean") this.fail(path, "expected a boolean");
    return value;
  }

  finite(value: unknown, path: string): number {
    if (typeof value !== "number" || !Number.isFinite(value))
      this.fail(path, "expected a finite number");
    return value;
  }

  nonNegative(value: unknown, path: string): number {
    const result = this.finite(value, path);
    if (result < 0) this.fail(path, "must be non-negative");
    return result;
  }

  positive(value: unknown, path: string): number {
    const result = this.finite(value, path);
    if (result <= 0) this.fail(path, "must be positive");
    return result;
  }

  nonNegativeInt(value: unknown, path: string): number {
    const result = this.nonNegative(value, path);
    if (!Number.isSafeInteger(result))
      this.fail(path, "expected a non-negative integer");
    return result;
  }

  positiveInt(value: unknown, path: string): number {
    const result = this.positive(value, path);
    if (!Number.isSafeInteger(result))
      this.fail(path, "expected a positive integer");
    return result;
  }

  grid(value: unknown, path: string, nonNegative: boolean): GridPoint {
    const o = this.object(value, path, ["x", "y"]);
    const x = this.gridCoordinate(o.x, `${path}.x`, nonNegative);
    const y = this.gridCoordinate(o.y, `${path}.y`, nonNegative);
    return { x, y };
  }

  integerGrid(value: unknown, path: string, nonNegative: boolean): GridPoint {
    const point = this.grid(value, path, nonNegative);
    if (!Number.isInteger(point.x))
      this.fail(`${path}.x`, "expected an integer grid coordinate");
    if (!Number.isInteger(point.y))
      this.fail(`${path}.y`, "expected an integer grid coordinate");
    return point;
  }

  oneOf<const T extends readonly string[]>(
    value: unknown,
    path: string,
    options: T,
  ): T[number] {
    const result = this.string(value, path);
    if (!options.includes(result as T[number]))
      this.fail(
        path,
        `expected one of ${options.map((option) => `'${option}'`).join(", ")}`,
      );
    return result as T[number];
  }

  keyPath(parent: string, key: string): string {
    return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)
      ? `${parent}.${key}`
      : `${parent}[${JSON.stringify(key)}]`;
  }

  private gridCoordinate(
    value: unknown,
    path: string,
    nonNegative: boolean,
  ): number {
    const result = this.finite(value, path);
    if (nonNegative && result < 0)
      this.fail(path, "grid coordinate must be non-negative");
    if (!Number.isInteger(result * 2))
      this.fail(path, "grid coordinate must use whole or half cells");
    return result;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return false;
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
